import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useAppContext } from '@/hooks/useAppContext';
import type { NostrEvent } from '@nostrify/nostrify';

export interface ProjectComment {
  comment: NostrEvent;
  /** The kind 30617 coordinate (kind:pubkey:d-tag) */
  projectCoordinate: string;
  /** The d-tag of the referenced project */
  projectDTag: string;
  /** The pubkey of the project author */
  projectAuthorPubkey: string;
}

/**
 * Fetch NIP-22 (kind 1111) comments that a user has made on kind 30617 repositories.
 */
export function useUserProjectComments(userPubkey: string, limit = 100) {
  const { nostr } = useNostr();
  const { config } = useAppContext();

  return useQuery<ProjectComment[]>({
    queryKey: ['user-project-comments', userPubkey, limit],
    enabled: !!userPubkey,
    queryFn: async ({ signal }) => {
      // Query kind 1111 comments from the user that reference kind 30617 (repositories)
      const events = await nostr.query(
        [
          {
            kinds: [1111],
            authors: [userPubkey],
            '#K': ['30617'], // Root is a kind 30617 event
            limit,
          },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
      );

      // Parse the project references from the comments
      const projectComments: ProjectComment[] = events
        .map((comment) => {
          // Look for the A tag (root reference to kind 30617)
          const aTag = comment.tags.find((tag) => tag[0] === 'A');
          if (!aTag) return null;

          const coordinate = aTag[1];
          // Format: kind:pubkey:d-tag
          const parts = coordinate.split(':');
          if (parts.length < 3) return null;

          const [, pubkey, ...dTagParts] = parts;
          const dTag = dTagParts.join(':'); // Handle d-tags with colons

          return {
            comment,
            projectCoordinate: coordinate,
            projectDTag: dTag,
            projectAuthorPubkey: pubkey,
          };
        })
        .filter((item): item is ProjectComment => item !== null)
        .sort((a, b) => b.comment.created_at - a.comment.created_at);

      return projectComments;
    },
  });
}

/**
 * Fetch the repository event for a given coordinate.
 */
export function useProjectByCoordinate(coordinate: string | undefined) {
  const { nostr } = useNostr();
  const { config } = useAppContext();

  return useQuery<NostrEvent | null>({
    queryKey: ['project-by-coordinate', coordinate],
    enabled: !!coordinate,
    queryFn: async ({ signal }) => {
      if (!coordinate) return null;

      const parts = coordinate.split(':');
      if (parts.length < 3) return null;

      const [kindStr, pubkey, ...dTagParts] = parts;
      const kind = parseInt(kindStr, 10);
      const dTag = dTagParts.join(':');

      if (isNaN(kind)) return null;

      const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);

      const events = await nostr
        .group(gitRelays)
        .query(
          [
            {
              kinds: [kind],
              authors: [pubkey],
              '#d': [dTag],
              limit: 1,
            },
          ],
          { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
        );

      return events[0] ?? null;
    },
  });
}
